import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Row, Col, Card, Button, Tabs, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { DatePicker } from '@app/common/DatePicker';
import { Can } from '@app/modules/Auth/components';
import { Page } from '@app/common/Page';
import DetailedChart from './DetailedChart';
import { BulbTwoTone } from '@ant-design/icons';
import './Report.less';
import { NumberCard } from '@app/common/NumberCard';
import { Color } from '@app/themes/colors';
import { useStores } from '@app/hooks/stores';
import ReportStatus from './ReportStatus';
import { CSV_MAPPING, REPORT_TAB_CSV_HEADERS } from '@app/constants';
import { RoomsDataInterface } from '@app/types/buildings';
import CsvReportDownload from '@app/common/CsvReportDownload';
import Spinner from '@app/common/Spinner/Spinner';

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(quarterOfYear);

const { RangePicker } = DatePicker;

const { TabPane } = Tabs;

const reportIntervalMs = 10 * 60 * 1000; //10min

const Report: React.FC = () => {
  const [date, setDate] = useState<Array<Dayjs | null>>([null, null]);
  const { dashboardStore, reportStore } = useStores();

  const {
    report,
    loading: overallLoading,
    getGraphReport,
    dubaiTZ,
    clearReportStore,
    isOneWeekGraphPeriod
  } = reportStore;

  const { buildings, getAllBuildings, setSelectBuildingId, loading } = dashboardStore;

  useEffect(() => {
    getAllBuildings();
  }, []);

  const onTabChange = key => {
    setSelectBuildingId(key);
    setDate([null, null]);
    clearReportStore();
  };

  const handleGetReport = (e: FormEvent, buildingId: number) => {
    e.preventDefault();
    getGraphReport(date, buildingId);
  };

  const handleDateChange = dates => {
    if (!dates) {
      clearReportStore();
      return setDate([null, null]);
    }
    setDate(dates);
  };

  const setWeekRange = () => {
    setDate([dayjs().utcOffset(dubaiTZ).subtract(7, 'day'), dayjs().utcOffset(dubaiTZ).subtract(1, 'day')]);
  };

  const setMonthRange = () => {
    setDate([dayjs().utcOffset(dubaiTZ).subtract(1, 'month'), dayjs().utcOffset(dubaiTZ).subtract(1, 'day')]);
  };

  const set24HRange = () => {
    setDate([dayjs().utcOffset(dubaiTZ).subtract(1, 'day'), dayjs().utcOffset(dubaiTZ).subtract(1, 'day')]);
  };

  const generateCSVDataFromReports = (reports?: Array<RoomsDataInterface>) => {
    if (!reports) return [];
    return reports.reduce((acc, item) => {
      let tempStorage = {};
      Object.keys(item).forEach(key => {
        if (REPORT_TAB_CSV_HEADERS.includes(key)) tempStorage[CSV_MAPPING[key]] = item[key];
      });

      acc.push(tempStorage);
      return acc;
    }, [] as Array<any>);
  };
  const csvData = generateCSVDataFromReports(report?.roomsData);

  const memoizedReportData = useMemo(() => {
    return (report?.graphData || []).map(row => ({
      interval: row.day,
      elapsed: row.sumAEnergy
    }));
  }, [report?.graphData]);

  if (loading) return <Spinner />;

  return (
    <Page className="report">
      <Tabs className="dashboard" onChange={onTabChange}>
        {buildings &&
          buildings.map(building => (
            <TabPane tab={building.name} key={building.id} disabled={overallLoading}>
              <Row gutter={24}>
                <Col lg={8} md={24}>
                  <Row gutter={24}>
                    <Col lg={24} md={24}>
                      <Card
                        className="summaryCard"
                        title="Period"
                        headStyle={{
                          color: '@cyan-6'
                        }}
                      >
                        <div className="report-buttons-block">
                          <Button onClick={set24HRange} disabled={overallLoading}>
                            Yesterday
                          </Button>
                          <Button onClick={setWeekRange} disabled={overallLoading}>
                            Last week
                          </Button>
                          <Button onClick={setMonthRange} disabled={overallLoading}>
                            Last month
                          </Button>
                        </div>
                        <div className="form-group">
                          <RangePicker
                            disabled={overallLoading}
                            style={{ width: '100%' }}
                            size="large"
                            value={[date[0], date[1]]}
                            ranges={{
                              'Last 2 weeks': [dayjs().subtract(13, 'day'), dayjs()],
                              'This Month': [dayjs().startOf('month'), dayjs()]
                            }}
                            onChange={handleDateChange}
                          />
                        </div>
                        <Row>
                          <Can
                            action="reports/overall"
                            yes={() => (
                              <Button
                                loading={overallLoading}
                                type="primary"
                                size="middle"
                                block
                                onClick={e => handleGetReport(e, building.id)}
                              >
                                Get report
                              </Button>
                            )}
                            no={() => (
                              <Button type="primary" size="middle" block disabled>
                                <i className="fa fa-lock" /> Get report
                              </Button>
                            )}
                          />
                        </Row>
                      </Card>
                    </Col>
                    <Col lg={24} md={24} sm={24}>
                      <NumberCard
                        icon={<BulbTwoTone />}
                        title="Building consumption"
                        loading={overallLoading}
                        postfix="kWh"
                        number={Number(report?.totalConsumption) || 0}
                      />
                    </Col>
                    <Col lg={24} md={24} sm={24}>
                      <NumberCard
                        icon={<BulbTwoTone twoToneColor={Color.lightGreen} />}
                        title="Building savings"
                        loading={overallLoading}
                        postfix="kWh"
                        number={Number(report?.siteSavings) || 0}
                        color="#52c41a"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col lg={16} md={24} sm={24}>
                  <Row gutter={24}>
                    <Col lg={24} md={24} sm={24}>
                      <Card title="Power consumption" loading={overallLoading}>
                        {isOneWeekGraphPeriod ? (
                          <DetailedChart reportData={memoizedReportData} timeZoneOffset={dubaiTZ} />
                        ) : (
                          <Tag color="blue">Histogram available only for 7 day reports</Tag>
                        )}
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col lg={24} md={24} sm={24}>
                  <Card
                    title="Building Report"
                    className="table-card"
                    extra={<CsvReportDownload csvData={csvData} fileName="building-report-total-values" />}
                  >
                    <ReportStatus />
                  </Card>
                </Col>
              </Row>
            </TabPane>
          ))}
      </Tabs>
    </Page>
  );
};
export default observer(Report);
